import { useState } from 'react'
import type { AllocationCategoryTemplate, AllocationMetricMode, AllocationPreviewContent, AllocationRow, AllocationSegment } from '../types/allocationPreview.types'
import { useElitePlansLanding } from '../ElitePlansLandingContext'
import { ElitePlansIcon } from '../icons/iconRegistry'
import { RightArrowIcon } from '../icons/RightArrowIcon'
import { SwapVerticalIcon } from '../icons/SwapVerticalIcon'
import { ElitePlansEventId, trackElitePlansClick } from '../analytics'
import {
  ELITE_PLANS_IN_REVIEW_RIVE,
  ELITE_PLANS_RESPONSIVE_ALLOCATION_RIVE,
  ELITE_PLANS_RISK_PROFILE_ICON_BASE
} from '../constants/assets'
import { isStrategyQuestionnaireSubmittedStatus } from './advice-cards/adviceCardStrategyStatusDisplay'
import RiveComponent, { RiveFit } from '../../../components/RiveComponent'
import { useRefetchStatusAfterStrategyQuestionnaire } from '../hooks/useRefetchStatusAfterStrategyQuestionnaire'
import { postCTA as postCta } from '../../../utils/appBridge'
import { roundOffAndFormatNumberWithCurrency, toPascalCaseWithSpaces } from '../../../utils/utils'
import {
  ElitePrimeRiskProfile,
  ElitePrimeStrategyStatus,
  getElitePrimeAllocationCategoryLabel,
  type ElitePrimeAllocationCategory,
  type ElitePrimeInsightCta,
  type ElitePrimeStrategyData
} from '../../../types/elitePrimeSummary.types'
import { ElitePlansAllocationPreviewSkeleton } from './ElitePlansAllocationPreviewSkeleton'

/** Cap visible bar/list rows; when exceeded, last row aggregates the rest. */
const MAX_VISIBLE_ALLOCATION_SEGMENTS = 6
const MAX_INDIVIDUAL_ALLOCATION_SEGMENTS = MAX_VISIBLE_ALLOCATION_SEGMENTS - 1

const FALLBACK_ALLOCATION_CATEGORY_COLORS = [
  '#1470CC',
  '#4D9FE8',
  '#A9D3F5',
  '#E8B36A',
  '#A99BE8',
  '#7FD4C1',
  '#95A5A6',
  '#E67E7E',
  '#5DADE2',
  '#58D68D',
  '#F5B041',
  '#AF7AC5',
  '#48C9B0',
  '#EC7063',
  '#85929E',
  '#D4AC0D'
] as const

type BuiltAllocationCategory = {
  id: string
  label: string
  color: string
  fromPercent: number
  fundCount: number
  toPercentage?: number
  toFundCount?: number
}

export function ElitePlansAllocationPreview() {
  const { activeTab, status, isStatusLoading } = useElitePlansLanding()
  const allocationPreview = activeTab.allocationPreview
  if (!allocationPreview) return null

  const planId = activeTab.id
  const strategyData = status?.elitePrimeInsights?.strategyData
  const strategyStatus =
    strategyData?.status ?? ElitePrimeStrategyStatus.QUESTIONNAIRE_NOT_INITIATED
  const findStrategyCopy = allocationPreview.findStrategy

  if (isStatusLoading) {
    return <ElitePlansAllocationPreviewSkeleton />
  }

  if (strategyStatus !== ElitePrimeStrategyStatus.PLAN_ROLLED_OUT && findStrategyCopy) {
    return (
      <ElitePlansFindStrategyPreview
        planId={planId}
        strategyStatus={strategyStatus}
        copy={findStrategyCopy}
        planAvailableInDays={strategyData?.planAvailableInDays}
        strategyCta={strategyData?.cta}
      />
    )
  }

  return (
    <ElitePlansRolledOutAllocationPreview
      planId={planId}
      allocationPreview={allocationPreview}
      strategyData={strategyData}
      viewDetailsCta={strategyData?.cta}
    />
  )
}

function ElitePlansRolledOutAllocationPreview({
  planId,
  allocationPreview,
  strategyData,
  viewDetailsCta
}: {
  planId: string
  allocationPreview: AllocationPreviewContent
  strategyData?: ElitePrimeStrategyData
  viewDetailsCta?: ElitePrimeInsightCta
}) {
  const [metricMode, setMetricMode] = useState<AllocationMetricMode>(() =>
    resolveDefaultAllocationMetricMode(strategyData?.allocationCategories)
  )
  const strategyName = strategyData?.riskProfile
    ? toPascalCaseWithSpaces(strategyData.riskProfile)
    : ''
  const riskProfile = strategyData?.riskProfile ?? ElitePrimeRiskProfile.BALANCED
  const riskProfileIconKey =
    riskProfile === ElitePrimeRiskProfile.VERY_CONSERVATIVE
      ? ElitePrimeRiskProfile.CONSERVATIVE
      : riskProfile
  const strategyIconSrc = `${ELITE_PLANS_RISK_PROFILE_ICON_BASE}/${riskProfileIconKey.toLowerCase().replaceAll('_', '-')}.svg`
  const corePortfolioValue =
    strategyData?.corePortfolioAmount != null
      ? roundOffAndFormatNumberWithCurrency(strategyData.corePortfolioAmount)
      : ''
  const otherPortfoliosValue =
    strategyData?.otherPortfolioAmount != null
      ? roundOffAndFormatNumberWithCurrency(strategyData.otherPortfolioAmount)
      : ''
  const { segments, rows } = buildAllocationPreviewCategories(
    allocationPreview.categoryItems,
    strategyData?.allocationCategories
  )
  const allocationToggleLabel = allocationPreview.allocationToggleLabel ?? ''
  const fundCountToggleLabel = allocationPreview.fundCountToggleLabel ?? 'Fund count'
  const showMetricToggle = Boolean(allocationToggleLabel)

  return (
    <section className='mx-5'>
      {allocationPreview.statusLabel ? (
        <p
          className='flex items-center justify-center gap-1.5 text-13 font-medium text-grey10 mb-1'
          style={
            allocationPreview.statusColor
              ? { color: allocationPreview.statusColor }
              : undefined
          }
        >
          <ElitePlansIcon
            iconKey='checkReady'
            {...(allocationPreview.statusColor
              ? { fill: allocationPreview.statusColor }
              : {})}
          />
          {allocationPreview.statusLabel}
        </p>
      ) : null}

      <h2 className='text-center text-[20px] font-normal leading-[1.3] text-grey10'>
        {allocationPreview.title}
      </h2>

      <div className='mt-7 rounded-2xl bg-[color:var(--ep-card)] shadow-[var(--ep-shadow)] border border-grey80'>
        <div className='flex items-center justify-between gap-3 px-4 py-3.5'>
          <div className='flex items-center gap-2'>
            <img
              src={strategyIconSrc}
              alt=''
              width={18}
              height={18}
              className='size-[18px] object-contain'
              aria-hidden='true'
            />
            <p className='text-sm font-medium text-grey10'>
              {strategyName}
            </p>
          </div>
          {showMetricToggle ? (
            <AllocationMetricToggle
              metricMode={metricMode}
              allocationLabel={allocationToggleLabel}
              fundCountLabel={fundCountToggleLabel}
              onToggle={() =>
                setMetricMode((current) =>
                  current === 'allocation' ? 'fundCount' : 'allocation'
                )
              }
            />
          ) : null}
        </div>

        <div className='border-t border-[color:var(--ep-divider,#EFF1F2)] px-4 pb-4 pt-3.5'>
          {allocationPreview.corePortfolioLabel ? (
            <div className='flex items-center justify-between text-[14px]'>
              <p className='text-grey10'>
                {allocationPreview.corePortfolioLabel}
              </p>
              <p className='font-medium text-grey10'>
                {corePortfolioValue}
              </p>
            </div>
          ) : null}

          <div
            className='mt-3.5 flex h-3 w-full gap-0.5'
            role='img'
            aria-label={segments.map((segment) => `${segment.label} ${segment.percent}%`).join(', ')}
          >
            {segments.map((segment, segmentIndex) => (
              <span
                key={segment.id}
                className={[
                  'h-full min-w-3',
                  segmentIndex === 0 ? 'rounded-l-[4px]' : '',
                  segmentIndex === segments.length - 1 ? 'rounded-r-[4px]' : ''
                ].join(' ')}
                style={{
                  flexGrow: Math.max(segment.percent, 0),
                  flexBasis: 0,
                  backgroundColor: segment.color
                }}
              />
            ))}
          </div>

          <ul className='mt-4 list-none space-y-2 p-0'>
            {rows.map((row) => (
              <AllocationPreviewRow key={row.id} row={row} metricMode={metricMode} />
            ))}
          </ul>
        </div>

        {allocationPreview.otherPortfoliosLabel ? (
          <div className='flex items-center justify-between border-t border-[color:var(--ep-divider,#EFF1F2)] px-4 py-3.5 text-[14px]'>
            <p className='text-grey10'>
              {allocationPreview.otherPortfoliosLabel}
            </p>
            <p className='font-medium text-grey10'>
              {otherPortfoliosValue}
            </p>
          </div>
        ) : null}
      </div>

      {allocationPreview.viewDetailsLabel ? (
        <AllocationPreviewOutlineButton
          label={allocationPreview.viewDetailsLabel}
          onClick={() => {
            trackElitePlansClick(ElitePlansEventId.ViewDetails, { planId })
            if (viewDetailsCta) {
              postCta(JSON.stringify(viewDetailsCta))
            }
          }}
        />
      ) : null}
    </section>
  )
}

function ElitePlansFindStrategyPreview({
  planId,
  strategyStatus,
  copy,
  planAvailableInDays,
  strategyCta
}: {
  planId: string
  strategyStatus: ElitePrimeStrategyStatus
  copy: NonNullable<AllocationPreviewContent['findStrategy']>
  planAvailableInDays?: number
  strategyCta?: ElitePrimeInsightCta
}) {
  const { postStrategyCta } = useRefetchStatusAfterStrategyQuestionnaire()
  const isSubmitted = isStrategyQuestionnaireSubmittedStatus(strategyStatus)
  const isInProgress = strategyStatus === ElitePrimeStrategyStatus.QUESTIONNAIRE_IN_PROGRESS
  const isNotInitiated = strategyStatus === ElitePrimeStrategyStatus.QUESTIONNAIRE_NOT_INITIATED

  let riveSrc: string | null = null
  if (isNotInitiated || isInProgress) {
    riveSrc = ELITE_PLANS_RESPONSIVE_ALLOCATION_RIVE
  } else if (isSubmitted) {
    riveSrc = ELITE_PLANS_IN_REVIEW_RIVE
  }

  const planAvailableDays = planAvailableInDays ?? 0
  const planAvailableDaysLabel =
    planAvailableDays === 1 ? '1 day' : `${planAvailableDays} days`
  const submittedTitle = copy.submitted.title.replace(
    '{planAvailableInDays}',
    planAvailableDaysLabel
  )

  let actionLabel = copy.notInitiated.ctaLabel
  if (isSubmitted) {
    actionLabel = copy.submitted.viewDetailsLabel
  } else if (isInProgress) {
    actionLabel = copy.inProgress.ctaLabel
  }

  const handleAction = () => {
    if (isSubmitted) {
      trackElitePlansClick(ElitePlansEventId.ViewDetails, { planId })
    } else {
      trackElitePlansClick(ElitePlansEventId.CtaClick, {
        planId,
        buttonLabel: actionLabel,
        landingPageState: ''
      })
    }

    if (strategyCta == null) return
    // Find CTA opens the questionnaire; submitted is View details
    postStrategyCta(strategyCta, { opensQuestionnaire: !isSubmitted })
  }

  return (
    <section className='mx-5'>
      <h2 className='text-center text-[20px] font-normal leading-[1.3] text-grey10'>
        {copy.sectionTitle}
      </h2>

      <div className='mt-4 overflow-hidden rounded-2xl border border-[#EFF1F2] bg-[color:var(--ep-card)] shadow-[var(--ep-shadow)]'>
        {riveSrc ? (
          <AllocationPreviewRivePanel src={riveSrc} ariaLabel={copy.sectionTitle} />
        ) : null}

        <div className='px-4 pb-4 pt-3.5 text-center'>
          {isNotInitiated ? (
            <p className='text-base leading-[1.45] text-[#667070]'>
              {copy.notInitiated.prefix}
              <span className='font-medium text-grey10'>
                {copy.notInitiated.emphasis}
              </span>
              {copy.notInitiated.suffix}
            </p>
          ) : null}

          {isInProgress ? (
            <p className='text-base leading-[1.45] text-[#667070]'>
              <span className='font-medium text-grey10'>
                {copy.inProgress.emphasis}
              </span>
              {copy.inProgress.body}
            </p>
          ) : null}

          {isSubmitted ? (
            <>
              <p className='text-[14px] font-semibold leading-[1.45] text-grey10'>
                {submittedTitle}
              </p>
              <p className='mt-1 text-[14px] leading-[1.45] text-[#667070]'>{copy.submitted.body}</p>
            </>
          ) : null}

          {isSubmitted ? (
            <AllocationPreviewOutlineButton label={actionLabel} onClick={handleAction} className='mt-4' />
          ) : (
            <button
              type='button'
              onClick={handleAction}
              className='ep-focus-ring mt-4 min-h-12 w-full rounded-2xl bg-black px-5 text-[14px] font-medium text-[color:var(--ep-cta-text)]'
            >
              {isInProgress ? copy.inProgress.ctaLabel : copy.notInitiated.ctaLabel}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

function AllocationPreviewRivePanel({
  src,
  ariaLabel
}: {
  src: string
  ariaLabel: string
}) {
  return (
    <div
      className='aspect-[337/231] w-full overflow-hidden bg-[#F3F5F5]'
      role='img'
      aria-label={ariaLabel}
    >
      <RiveComponent src={src} fit={RiveFit.COVER} className='h-full w-full' autoplay />
    </div>
  )
}

function AllocationPreviewOutlineButton({
  label,
  onClick,
  className = 'mt-4'
}: {
  label: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`ep-focus-ring min-h-12 w-full rounded-2xl border border-solid border-[#E4E6E8] bg-[color:var(--ep-card)] text-center text-[14px] font-medium text-grey10 ${className}`}
    >
      {label}
    </button>
  )
}

function AllocationPreviewRow({
  row,
  metricMode
}: {
  row: AllocationRow
  metricMode: AllocationMetricMode
}) {
  const fromValue = metricMode === 'fundCount' ? String(row.fundCount) : row.percent
  const hasTarget = row.toPercent != null && row.toFundCount != null

  let toValue: string | null = null
  if (hasTarget) {
    toValue = metricMode === 'fundCount' ? String(row.toFundCount) : row.toPercent
  }

  return (
    <li className='flex items-center gap-2.5 text-[13px] text-grey10'>
      <span
        className='h-2.5 w-2.5 shrink-0 rounded-full'
        style={{ backgroundColor: row.color }}
        aria-hidden='true'
      />
      <span className='flex-1 text-grey20'>{row.label}</span>
      <span className='font-medium'>{fromValue}</span>
      {hasTarget ? (
        <>
          <RightArrowIcon />
          <span className='font-medium'>{toValue}</span>
        </>
      ) : null}
      {row.trend === 'up' ? (
        <ElitePlansIcon iconKey='trendUp' className='text-[color:var(--ep-accent)]' />
      ) : null}
      {row.trend === 'down' ? (
        <ElitePlansIcon iconKey='trendDown' className='text-[color:var(--ep-accent)]' />
      ) : null}
    </li>
  )
}

function AllocationMetricToggle({
  metricMode,
  allocationLabel,
  fundCountLabel,
  onToggle
}: {
  metricMode: AllocationMetricMode
  allocationLabel: string
  fundCountLabel: string
  onToggle: () => void
}) {
  const label = metricMode === 'allocation' ? allocationLabel : fundCountLabel
  const nextLabel = metricMode === 'allocation' ? fundCountLabel : allocationLabel

  return (
    <button
      type='button'
      className='ep-focus-ring flex items-center gap-1 border-none bg-transparent p-0 text-[13px] font-medium text-grey10'
      aria-label={`Show ${nextLabel}`}
      onClick={onToggle}
    >
      <span>{label}</span>
      <SwapVerticalIcon />
    </button>
  )
}

function buildAllocationPreviewCategories(
  categoryItems: AllocationCategoryTemplate[],
  allocationCategories?: ElitePrimeStrategyData['allocationCategories']
): { segments: AllocationSegment[]; rows: AllocationRow[] } {
  const templateByName = new Map(
    categoryItems.map((item) => [item.categoryName, item])
  )

  // Response order is the source of truth for segment sequence.
  const orderedCategories = allocationCategories ?? []
  const built = orderedCategories.map((category, index) =>
    buildAllocationCategoryFromResponse(category, templateByName.get(category.name), index)
  )

  return toAllocationPreviewSegmentsAndRows(collapseAllocationCategories(built))
}

function buildAllocationCategoryFromResponse(
  category: ElitePrimeAllocationCategory,
  template: AllocationCategoryTemplate | undefined,
  index: number
): BuiltAllocationCategory {
  const fallbackColor =
    FALLBACK_ALLOCATION_CATEGORY_COLORS[index % FALLBACK_ALLOCATION_CATEGORY_COLORS.length]

  return {
    id: template?.id ?? category.name,
    label: getElitePrimeAllocationCategoryLabel(category.name),
    color: template?.color ?? fallbackColor,
    fromPercent: category.fromPercentage ?? 0,
    fundCount: category.fromFundCount ?? 0,
    toPercentage: category.toPercentage,
    toFundCount: category.toFundCount
  }
}

/**
 * At most 6 visible rows. With more than 5 categories, keep the first 5 and
 * merge the rest into `+{n} more` (summed percent + fund counts).
 */
function collapseAllocationCategories(
  categories: BuiltAllocationCategory[]
): BuiltAllocationCategory[] {
  if (categories.length <= MAX_INDIVIDUAL_ALLOCATION_SEGMENTS) {
    return categories
  }

  const head = categories.slice(0, MAX_INDIVIDUAL_ALLOCATION_SEGMENTS)
  const tail = categories.slice(MAX_INDIVIDUAL_ALLOCATION_SEGMENTS)
  const remainingCount = tail.length

  const combinedFromPercent = tail.reduce((sum, item) => sum + item.fromPercent, 0)
  const combinedFundCount = tail.reduce((sum, item) => sum + item.fundCount, 0)
  const allHaveTarget = tail.every(
    (item) => item.toPercentage != null && item.toFundCount != null
  )

  const combined: BuiltAllocationCategory = {
    id: 'allocation-more',
    label: `+${remainingCount} more`,
    color: tail[0]?.color ?? FALLBACK_ALLOCATION_CATEGORY_COLORS[0],
    fromPercent: combinedFromPercent,
    fundCount: combinedFundCount,
    ...(allHaveTarget
      ? {
        toPercentage: tail.reduce((sum, item) => sum + (item.toPercentage ?? 0), 0),
        toFundCount: tail.reduce((sum, item) => sum + (item.toFundCount ?? 0), 0)
      }
      : {})
  }

  return [...head, combined]
}

function toAllocationPreviewSegmentsAndRows(
  categories: BuiltAllocationCategory[]
): { segments: AllocationSegment[]; rows: AllocationRow[] } {
  const segments: AllocationSegment[] = []
  const rows: AllocationRow[] = []

  for (const category of categories) {
    const hasTarget = category.toPercentage != null && category.toFundCount != null

    segments.push({
      id: category.id,
      color: category.color,
      percent: category.fromPercent,
      label: category.label
    })

    rows.push({
      id: category.id,
      label: category.label,
      percent: formatAllocationPercentLabel(category.fromPercent),
      fundCount: category.fundCount,
      ...(hasTarget
        ? {
          toPercent: formatAllocationPercentLabel(category.toPercentage ?? 0),
          toFundCount: category.toFundCount
        }
        : {}),
      color: category.color
    })
  }

  return { segments, rows }
}

function formatAllocationPercentLabel(percent: number): string {
  return `${Math.round(percent)}%`
}

/** Default to fund count when total from/to fund counts differ by more than 5. */
function resolveDefaultAllocationMetricMode(
  allocationCategories?: ElitePrimeStrategyData['allocationCategories']
): AllocationMetricMode {
  if (!allocationCategories?.length) return 'allocation'

  let fromFundCountTotal = 0
  let toFundCountTotal = 0
  for (const category of allocationCategories) {
    fromFundCountTotal += category.fromFundCount ?? 0
    toFundCountTotal += category.toFundCount ?? 0
  }

  if (Math.abs(fromFundCountTotal - toFundCountTotal) > 5) {
    return 'fundCount'
  }
  return 'allocation'
}
